import { useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/use-auth'
import MicrosoftSignIn from '@/components/auth/microsoft-sign-in'

const BRAND = {
  green: '#7ABC64',
  greenDark: '#5A9C4A',
  navy: '#1E293B',
  navyDeep: '#0F172A',
  slate: '#334155',
} as const

const PVT_LOGO = 'https://perfectvirtualteam.com/wp-content/uploads/2023/01/perfect-virtual-team-logo-small.png'

// ── 100 daily tips for back-office teams & operations managers ──
const DAILY_TIPS: { category: string; tip: string }[] = [
  // ─── Meetings & Communication (1-15) ───
  { category: 'Meetings', tip: 'Start every meeting with a clear agenda. No agenda, no meeting — protect your team\'s time.' },
  { category: 'Meetings', tip: 'End meetings 5 minutes early. Give your team breathing room between back-to-back calls.' },
  { category: 'Meetings', tip: 'Assign a note-taker for every meeting. Action items without owners are just wishes.' },
  { category: 'Communication', tip: 'Replace "any update?" messages with specific questions. Vague check-ins create anxiety, not clarity.' },
  { category: 'Meetings', tip: 'If a meeting can be an email, make it an email. If an email needs discussion, make it a meeting.' },
  { category: 'Communication', tip: 'When giving feedback, lead with what\'s working before what needs fixing. People listen better after feeling valued.' },
  { category: 'Communication', tip: 'Write messages for the reader, not the writer. Ask yourself: will they understand this in 5 seconds?' },
  { category: 'Meetings', tip: 'The best stand-ups are under 10 minutes. Focus on blockers, not status reports everyone can read.' },
  { category: 'Communication', tip: 'Acknowledge messages even when you don\'t have an answer yet. "Looking into this" beats silence every time.' },
  { category: 'Meetings', tip: 'Record important meetings. Team members in different time zones shouldn\'t miss critical decisions.' },
  { category: 'Communication', tip: 'Use screen recordings instead of long written explanations. A 2-minute video saves 20 minutes of back-and-forth.' },
  { category: 'Meetings', tip: 'Start your day with a 5-minute self-standup: what\'s my #1 priority today? Everything else follows.' },
  { category: 'Communication', tip: 'Over-communicate during handoffs. The 2 minutes you spend explaining saves 20 minutes of confusion later.' },
  { category: 'Communication', tip: 'Don\'t CC people "just in case." Every unnecessary notification trains people to ignore important ones.' },
  { category: 'Meetings', tip: 'Weekly 1-on-1s build trust faster than any team-building exercise. 15 minutes of genuine listening goes far.' },

  // ─── Productivity & Time Management (16-35) ───
  { category: 'Productivity', tip: 'Do your hardest task in the first 90 minutes. Willpower is a depletable resource — use it wisely.' },
  { category: 'Productivity', tip: 'Batch similar tasks together. Context-switching between emails, reports, and calls costs 23 minutes each time.' },
  { category: 'Productivity', tip: 'The two-minute rule: if a task takes less than 2 minutes, do it now. Small tasks pile up into big stress.' },
  { category: 'Time Management', tip: 'Block 30 minutes of "no meeting" time daily for deep work. Protect it like you would a client call.' },
  { category: 'Productivity', tip: 'Touch each task only once. Read it, decide the next action, do it or schedule it — don\'t re-read it later.' },
  { category: 'Time Management', tip: 'Plan tomorrow before leaving today. Arriving with a clear plan beats 30 minutes of morning drift.' },
  { category: 'Productivity', tip: 'Use checklists for recurring processes. Your brain should solve problems, not remember steps.' },
  { category: 'Time Management', tip: 'Say no to low-impact tasks politely but firmly. Every yes to something unimportant is a no to something that matters.' },
  { category: 'Productivity', tip: 'Close unnecessary browser tabs and apps. Digital clutter is mental clutter — clean workspace, clear mind.' },
  { category: 'Time Management', tip: 'Estimate how long tasks take, then track actual time. You\'ll be surprised — and start planning realistically.' },
  { category: 'Productivity', tip: 'Don\'t check email first thing in the morning. Start with your priorities, not other people\'s priorities.' },
  { category: 'Time Management', tip: 'Use the Pomodoro technique for tedious tasks: 25 minutes focused, 5 minutes break. Monotony kills momentum.' },
  { category: 'Productivity', tip: 'Automate anything you do more than 3 times a week. Your time is better spent on decisions, not repetition.' },
  { category: 'Productivity', tip: 'End each day by writing down 3 things you accomplished. Progress you don\'t notice still counts.' },
  { category: 'Time Management', tip: 'Deadlines without buffers are promises you\'ll break. Add 20% buffer time to every estimate you give.' },
  { category: 'Productivity', tip: 'Keep a "parking lot" for ideas that pop up while working. Capture them, then return to focus — don\'t chase shiny objects.' },
  { category: 'Time Management', tip: 'Review your calendar every Sunday evening. A prepared week always outperforms a reactive one.' },
  { category: 'Productivity', tip: 'The best process is the one your team actually follows. Perfection on paper means nothing if it\'s ignored in practice.' },
  { category: 'Productivity', tip: 'Set up templates for recurring reports and emails. The 30 minutes you invest now saves hours every month.' },
  { category: 'Time Management', tip: 'Learn to distinguish urgent from important. Urgent tasks shout; important tasks whisper — listen carefully.' },

  // ─── Team Management & Leadership (36-55) ───
  { category: 'Leadership', tip: 'Trust is built in drops and lost in buckets. Follow through on small promises — your team is always watching.' },
  { category: 'Team Management', tip: 'Praise publicly, correct privately. Humiliation doesn\'t build better performers — it builds resentment.' },
  { category: 'Leadership', tip: 'Your team mirrors your energy. Walk in calm and prepared, and the floor follows. Walk in stressed, and chaos spreads.' },
  { category: 'Team Management', tip: 'Don\'t solve every problem for your team. Ask "what do you think we should do?" first — grow thinkers, not followers.' },
  { category: 'Leadership', tip: 'The best managers remove obstacles, not add rules. Ask daily: what\'s blocking my team right now?' },
  { category: 'Team Management', tip: 'Rotate responsibilities within the team quarterly. Cross-training builds resilience — no single point of failure.' },
  { category: 'Leadership', tip: 'When a team member makes a mistake, ask "what can we change in the process?" — not "why did you do that?"' },
  { category: 'Team Management', tip: 'Set clear expectations on day one. Most performance issues are really clarity issues in disguise.' },
  { category: 'Leadership', tip: 'Celebrate small wins weekly. People who feel appreciated will always do more than what\'s expected.' },
  { category: 'Team Management', tip: 'New team members form their work habits in the first 30 days. Make onboarding thorough — it pays dividends forever.' },
  { category: 'Leadership', tip: 'Share the "why" behind decisions, not just the "what." People execute better when they understand the purpose.' },
  { category: 'Team Management', tip: 'Track your team\'s workload distribution. Burnout doesn\'t announce itself — it shows up as missed deadlines and silence.' },
  { category: 'Leadership', tip: 'Be the manager who asks "how can I help?" instead of "where\'s the update?" — the results will surprise you.' },
  { category: 'Team Management', tip: 'Pair experienced team members with newer ones. The best training happens peer-to-peer, not in formal sessions.' },
  { category: 'Leadership', tip: 'Your team will never care about quality more than you do. Set the standard by example, every single day.' },
  { category: 'Team Management', tip: 'Schedule skip-level check-ins monthly. The view from the front line often reveals what reports won\'t.' },
  { category: 'Leadership', tip: 'Consistency beats intensity. A manager who\'s steadily reliable is worth ten who are brilliant but unpredictable.' },
  { category: 'Team Management', tip: 'When workload spikes, communicate the priority order clearly. Your team can\'t read your mind about what matters most.' },
  { category: 'Leadership', tip: 'Admit when you don\'t know something. Vulnerability from a leader isn\'t weakness — it\'s permission for the team to be human.' },
  { category: 'Team Management', tip: 'Review and update SOPs every quarter. Outdated procedures frustrate good people and protect bad habits.' },

  // ─── Process & Operations (56-75) ───
  { category: 'Operations', tip: 'Document what only one person knows. Every "only Raj knows how to do this" is a risk hiding in plain sight.' },
  { category: 'Process', tip: 'Before adding a new step to any process, remove an unnecessary one. Complexity is the enemy of execution.' },
  { category: 'Operations', tip: 'Run a weekly quality check on 5% of your team\'s output. Prevention is cheaper than correction.' },
  { category: 'Process', tip: 'If an escalation happens more than twice for the same reason, fix the root cause — not the symptom.' },
  { category: 'Operations', tip: 'Track your team\'s first-response time. Speed of acknowledgment matters as much as speed of resolution.' },
  { category: 'Process', tip: 'Create a "lessons learned" log after every major issue. The same mistake twice isn\'t a mistake — it\'s a choice.' },
  { category: 'Operations', tip: 'Audit your tools monthly. Are you paying for 10 tools when 3 could do the job? Simplify ruthlessly.' },
  { category: 'Process', tip: 'Map your end-to-end workflow at least once a quarter. Bottlenecks hide where handoffs happen.' },
  { category: 'Operations', tip: 'Set up alerts for SLA breaches before they happen, not after. Proactive beats reactive, always.' },
  { category: 'Process', tip: 'Standardize naming conventions for files, tickets, and projects. "Final_v2_REAL_final" helps nobody.' },
  { category: 'Operations', tip: 'Measure what matters: accuracy, turnaround time, and client satisfaction. Everything else is vanity metrics.' },
  { category: 'Process', tip: 'Every process should have a clear owner. Shared ownership means nobody owns it.' },
  { category: 'Operations', tip: 'Build a knowledge base for FAQs your team asks repeatedly. Answer once, save time forever.' },
  { category: 'Process', tip: 'When a process takes more than 7 steps, question every step. Simplicity scales; complexity breaks.' },
  { category: 'Operations', tip: 'Your backup plan should be tested, not just written. An untested backup plan is just a document.' },
  { category: 'Process', tip: 'Use color coding in dashboards: green/amber/red at a glance saves digging through data.' },
  { category: 'Operations', tip: 'Schedule a monthly process improvement hour. Small tweaks every month compound into massive gains by year-end.' },
  { category: 'Process', tip: 'Every report you generate should answer one question clearly. If it doesn\'t, it\'s noise — not insight.' },
  { category: 'Operations', tip: 'Cross-train at least 2 people on every critical process. Vacations and sick days shouldn\'t cause emergencies.' },
  { category: 'Process', tip: 'Ask your team what frustrates them most about current processes. The best improvements come from the people doing the work.' },

  // ─── Client & Stakeholder Management (76-85) ───
  { category: 'Client Management', tip: 'Under-promise and over-deliver. A "done early" email always lands better than a "need more time" one.' },
  { category: 'Client Management', tip: 'Send proactive updates before clients ask. The question "any update?" means you\'ve already waited too long.' },
  { category: 'Client Management', tip: 'When delivering bad news, bring a solution with it. Problems without proposals just create anxiety.' },
  { category: 'Client Management', tip: 'Learn your client\'s priorities — not just their requests. The best operators solve the problem behind the question.' },
  { category: 'Client Management', tip: 'Keep a log of client preferences and pain points. Personalized service isn\'t a gift — it\'s a competitive advantage.' },
  { category: 'Client Management', tip: 'Respond to client escalations within 1 hour, even if just to say "I\'m looking into this." Speed signals respect.' },
  { category: 'Client Management', tip: 'Share wins with your clients quarterly — not just issues. Remind them why they chose your team.' },
  { category: 'Client Management', tip: 'Ask clients for feedback regularly, not just when things go wrong. Prevention is always cheaper than recovery.' },
  { category: 'Client Management', tip: 'When onboarding a new client, over-invest in the first 30 days. First impressions set the tone for years.' },
  { category: 'Client Management', tip: 'Document every client commitment in writing. Memory is unreliable — written records prevent "I never agreed to that."' },

  // ─── Growth & Self-Development (86-100) ───
  { category: 'Growth', tip: 'Read for 15 minutes daily about your industry. The person who stops learning starts falling behind — quietly.' },
  { category: 'Growth', tip: 'Ask for feedback from your own manager quarterly. Growth isn\'t just for your team — it\'s for you too.' },
  { category: 'Growth', tip: 'Learn one new keyboard shortcut every week. Small efficiencies compound — you\'ll save hours by year-end.' },
  { category: 'Growth', tip: 'Shadow a colleague in a different department once a quarter. Cross-functional understanding makes you a better operator.' },
  { category: 'Growth', tip: 'Keep a decision journal. Write down what you decided, why, and what happened — patterns will emerge.' },
  { category: 'Growth', tip: 'Your career grows at the speed of the problems you can solve. Volunteer for the hard ones.' },
  { category: 'Growth', tip: 'Teach what you know to someone junior. Teaching deepens your own understanding and builds your reputation.' },
  { category: 'Growth', tip: 'Master Excel or Sheets beyond the basics. Pivot tables, VLOOKUP, and conditional formatting separate good from great.' },
  { category: 'Growth', tip: 'Practice writing concise emails. If it takes 3 paragraphs, you haven\'t thought it through yet.' },
  { category: 'Growth', tip: 'Set one professional goal every quarter. Without a target, you\'re just busy — not progressing.' },
  { category: 'Growth', tip: 'Take breaks seriously. A tired mind makes poor decisions — rest is productive, not lazy.' },
  { category: 'Growth', tip: 'Build relationships with people outside your team. Opportunities and solutions often come from unexpected connections.' },
  { category: 'Growth', tip: 'Learn to say "I\'ll get back to you" instead of guessing. Accuracy builds more trust than speed.' },
  { category: 'Growth', tip: 'Reflect on your biggest mistake this month and what you\'d do differently. Reflection without action is just regret.' },
  { category: 'Growth', tip: 'Start tomorrow better than you started today. Small improvements, compounded daily, create extraordinary results.' },
]

// Pick a random tip on every page load
function getRandomTip() {
  return DAILY_TIPS[Math.floor(Math.random() * DAILY_TIPS.length)]
}

// Category → icon + color mapping
const CATEGORY_STYLES: Record<string, { icon: string; color: string }> = {
  'Meetings': { icon: '🗓', color: '#3B82F6' },
  'Communication': { icon: '💬', color: '#8B5CF6' },
  'Productivity': { icon: '⚡', color: '#F59E0B' },
  'Time Management': { icon: '⏱', color: '#3B82F6' },
  'Leadership': { icon: '🎯', color: '#7ABC64' },
  'Team Management': { icon: '👥', color: '#7ABC64' },
  'Operations': { icon: '⚙', color: '#F59E0B' },
  'Process': { icon: '📋', color: '#8B5CF6' },
  'Client Management': { icon: '🤝', color: '#3B82F6' },
  'Growth': { icon: '📈', color: '#7ABC64' },
}

export default function SignIn() {
  const { auth } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    if (!auth.isLoading && auth.user) {
      // Respect the `redirect` query param set by ProtectedRoute when an
      // unauthenticated user tried to access a protected path. Without this
      // honouring, every refresh bounces to /redirect → /dashboard, losing
      // the page the user was actually on.
      const target = searchParams.get('redirect')
      const safe = target && target.startsWith('/') && !target.startsWith('//') ? target : '/redirect'
      navigate(safe, { replace: true })
    }
  }, [auth.isLoading, auth.user, navigate, searchParams])

  const error = searchParams.get('error')
  const todaysTip = useMemo(getRandomTip, [])
  const style = CATEGORY_STYLES[todaysTip.category] || { icon: '💡', color: BRAND.green }

  return (
    <div className="min-h-dvh flex flex-col lg:flex-row">
      {/* ── LEFT PANEL: Hero + Daily Tip ── */}
      <div
        className="relative lg:w-[60%] flex flex-col justify-between lg:justify-center lg:items-center p-8 lg:p-12 xl:p-16 overflow-hidden"
        style={{ background: BRAND.navyDeep }}
      >
        {/* Subtle grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}
        />

        {/* Green accent line — left edge (desktop) / top edge (mobile) */}
        <div
          className="absolute top-0 left-0 right-0 h-[3px] lg:h-full lg:w-[3px] lg:right-auto z-10"
          style={{ background: `linear-gradient(90deg, ${BRAND.green}, ${BRAND.greenDark})` }}
        />

        {/* Floating glow orbs */}
        <div
          className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-[0.07]"
          style={{ background: BRAND.green }}
        />
        <div
          className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full blur-3xl opacity-[0.05]"
          style={{ background: '#3B82F6' }}
        />

        {/* Logo: flow on mobile, absolute top-left on desktop */}
        <div className="relative z-10 flex items-center gap-3 mb-6 lg:absolute lg:top-8 lg:left-12 lg:mb-0">
          <img src={PVT_LOGO} alt="Perfect Virtual Team" className="h-8 lg:h-9" />
        </div>

        {/* Daily tip section */}
        <div className="relative z-10 lg:text-center max-w-xl">
          {/* Category badge */}
          <div className="flex lg:justify-center mb-4 lg:mb-5">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
              style={{
                color: style.color,
                background: `${style.color}15`,
                border: `1px solid ${style.color}25`,
              }}
            >
              <span>{style.icon}</span>
              {todaysTip.category}
            </span>
          </div>

          {/* Tip label */}
          <p
            className="text-xs lg:text-sm font-semibold uppercase tracking-[0.15em] mb-3 lg:mb-4"
            style={{ color: `${BRAND.green}90` }}
          >
            Quick Tip
          </p>

          {/* The tip itself */}
          <blockquote className="text-white text-lg lg:text-2xl xl:text-[1.75rem] font-semibold leading-snug lg:leading-relaxed tracking-tight">
            &ldquo;{todaysTip.tip}&rdquo;
          </blockquote>

          {/* Gradient divider */}
          <div
            className="mx-auto lg:mx-auto mt-6 mb-4 w-16 lg:w-24 h-[2px] rounded-full"
            style={{ background: `linear-gradient(90deg, transparent, ${BRAND.green}, transparent)` }}
          />

          <p className="text-white/40 text-xs lg:text-sm font-medium leading-relaxed">
            A fresh tip every time you sign in — lead, operate, and grow.
          </p>
        </div>

        {/* Bottom section */}
        <div className="relative z-10 mt-8 lg:mt-0 lg:absolute lg:bottom-8 lg:left-0 lg:right-0 lg:flex lg:flex-col lg:items-center lg:gap-3 space-y-4 lg:space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex -space-x-1.5">
              {[
                { initials: 'OP', opacity: 0.25 },
                { initials: 'QD', opacity: 0.20 },
                { initials: 'RS', opacity: 0.15 },
              ].map(({ initials, opacity }, i) => (
                <div
                  key={i}
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{
                    background: `rgba(122,188,100,${opacity})`,
                    border: `2px solid ${BRAND.navyDeep}`,
                  }}
                >
                  <span className="text-white/70 text-[9px] font-bold">{initials}</span>
                </div>
              ))}
            </div>
            <p className="text-white/30 text-xs">
              Perfect Virtual Team — Operations
            </p>
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL: Sign In ── */}
      <div className="lg:w-[40%] flex flex-col justify-center items-center p-8 lg:p-12 xl:p-16 bg-white">
        <div className="w-full max-w-[380px] space-y-8">
          {/* Logo — visible on desktop right panel */}
          <div className="hidden lg:flex items-center gap-2.5">
            <img src={PVT_LOGO} alt="Perfect Virtual Team" className="h-10 lg:h-12" />
          </div>

          {/* Heading */}
          <div>
            <h2
              className="text-2xl font-bold tracking-tight"
              style={{ color: BRAND.navy }}
            >
              Welcome back
            </h2>
            <p className="text-slate-500 text-sm mt-1.5 leading-relaxed">
              Sign in with your company Microsoft account to access the dashboard.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 shrink-0 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Divider line */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-[11px] font-medium text-slate-400 uppercase tracking-wider">
                Company login
              </span>
            </div>
          </div>

          {/* Microsoft Sign In */}
          <MicrosoftSignIn />

          {/* Security badge */}
          <div className="flex items-center justify-center gap-2 text-slate-400">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span className="text-[11px]">
              Secured with Microsoft Azure Active Directory
            </span>
          </div>

          {/* Access note */}
          <div
            className="rounded-xl px-4 py-3 text-center"
            style={{ background: `${BRAND.green}08`, border: `1px solid ${BRAND.green}15` }}
          >
            <p className="text-xs text-slate-500 leading-relaxed">
              Only authorized <span className="font-semibold text-slate-600">Perfect Virtual Team</span> members can access this dashboard.
              Need access?{' '}
              <a
                href="mailto:sachin@perfectvirtualteam.com"
                className="font-medium hover:underline"
                style={{ color: BRAND.green }}
              >
                Contact admin
              </a>
            </p>
          </div>

          {/* Footer */}
          <div className="pt-2 text-center space-y-0.5">
            <p className="text-[11px] text-slate-400">Powered by</p>
            <p className="text-xs text-slate-500 font-medium">Perfect Virtual Team</p>
            <p className="text-[11px] text-slate-400">AIM Vision Technologies LLC, USA</p>
          </div>
        </div>
      </div>
    </div>
  )
}
